/**
 * HostEditor — редактор квизов ведущего на маршруте /edit (И5, план §2.1/И5).
 *
 * Архитектура «гибрид»: черновики живут в localStorage (drafts.ts); публикация и
 * старт сессии — на сервере. Экран делится на список черновиков (sidebar) и
 * редактор выбранного квиза (название + вопросы). Для каждого вопроса выбирается
 * тип (kind) с переключением формы под тип; опционально — живое preview через
 * существующие под-view components/views/* (mock QuestionPayload без правильного
 * ответа — анти-чит сохраняется). Действия: автосохранение (debounce), «Сохранить»,
 * «Экспорт JSON», «Импорт JSON», «Создать» (POST /api/games → onPublished() —
 * переход в кабинет /me; проведение квиза — кнопкой «Провести» из кабинета,
 * 0.12.1: редактор больше не запускает сессию автоматически).
 *
 * Привязка правки (Фаза 12, B1): черновик, созданный из серверного квиза, несёт
 * персистентное поле draft.gameId; для него главная кнопка — «Сохранить изменения»
 * (PUT /api/games/{id}), для непривязанного — «Создать» (POST).
 * Привязка живёт в localStorage, поэтому переживает выход/вход в /edit и не даёт
 * «Редактировать» плодить дубли квизов.
 *
 * 429 от rate-limit показывается дружелюбным сообщением.
 */
import { type ChangeEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ApiError,
  createGameFromDraft,
  encodeDraftQuiz,
  fetchOpenStatus,
  getEffectiveLimits,
  getGame,
  parseWireQuizPayload,
  updateGame,
  wireQuizToDraft,
  type EffectiveLimits,
} from '../api';
import {
  createDraft,
  deleteDraft,
  downloadDraft,
  findDraftByGameId,
  getDraft,
  getLastOpenedDraftId,
  importDraft,
  listDrafts,
  MAX_DRAFTS,
  saveDraft,
  setLastOpenedDraft,
  type DraftMeta,
  type DraftQuestion,
  type QuizDraft,
} from '../drafts';
import { errorMessage, formatBytes } from '../util';
import { EditorJsonInsert, EditorJsonPanel, QuizQuestionList } from '../components/quiz';
import { Link } from '../components/ui';

/** Шаг автосохранения (мс): после прекращения правок черновик пишется в localStorage. */
const AUTOSAVE_DEBOUNCE_MS = 800;

/**
 * Сигнатура КОНТЕНТА черновика (id/название/вопросы/привязка) без updatedAt.
 * По ней автосохранение отличает реальные правки от простого ОТКРЫТИЯ черновика:
 * открытие не должно писать в localStorage и поднимать запись наверх списка.
 */
function draftContentKey(d: QuizDraft): string {
  return JSON.stringify([d.id, d.title, d.gameId ?? null, d.questions]);
}

export interface HostEditorProps {
  /**
   * Вызывается после успешной публикации новой игры (POST /api/games). 0.12.1:
   * редактор уходит в кабинет /me — квиз появляется в «Мои квизы», проведение —
   * кнопкой «Провести» там же (раньше публикация сразу запускала host-флоу).
   */
  onPublished: () => void;
  /** Выход из редактора (обычно — на главную или в кабинет). */
  onExit: () => void;
  /**
   * ID игры для правки: разовый deeplink из кабинета («Редактировать»). Если
   * задан — редактор открывает/актуализирует черновик, привязанный к этой игре
   * (draft.gameId, B1): существующий переиспользуется (копии не плодятся),
   * отсутствующий создаётся с привязкой. Само решение PUT/POST живёт в ПЕРСИСТЕНТНОМ
   * поле draft.gameId (не в этом пропе): привязка переживает выход из редактора
   * и прямой заход в /edit. null/undefined — обычный вход без deeplink.
   */
  editGameId?: number | null;
  /** Завершено редактирование (App может сбросить editGameId, если нужно). */
  onEditConsumed?: () => void;
}

export function HostEditor({
  onPublished,
  onExit,
  editGameId,
  onEditConsumed,
}: HostEditorProps): JSX.Element {
  const { t } = useTranslation();
  const [drafts, setDrafts] = useState<DraftMeta[]>([]);
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [draft, setDraft] = useState<QuizDraft | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [showPreview, setShowPreview] = useState(true);
  // Модал «JSON» (Фаза 12, D3): живой предпросмотр черновика + справочник типов.
  const [jsonOpen, setJsonOpen] = useState(false);
  // Модал «Вставить из JSON» (Фаза 12, D4): вставка вопросов в конец черновика.
  const [insertOpen, setInsertOpen] = useState(false);

  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Лимиты (user-facing счётчики, Фаза 7, И6) ─────────────────────────────
  // Грузятся один раз из public /api/limits: вопросов N/limit (блокировка
  // добавления сверх лимита), размер картинок (hint лимита), char-счётчики.
  const [limits, setLimits] = useState<EffectiveLimits | null>(null);
  useEffect(() => {
    let cancelled = false;
    getEffectiveLimits()
      .then((l) => {
        if (!cancelled) setLimits(l);
      })
      .catch(() => {
        /* non-blocking: счётчики опциональны; редактор работает и без лимитов */
      });
    return () => {
      cancelled = true;
    };
  }, []);
  // Достигнут лимит вопросов → блокируем добавление новых.
  const questionsFull =
    limits !== null && limits.questionsPerQuiz > 0 && draft !== null
      ? draft.questions.length >= limits.questionsPerQuiz
      : false;
  // Достигнут лимит числа черновиков (MAX_DRAFTS) → блокируем «Новый квиз» и импорт.
  const draftsFull = drafts.length >= MAX_DRAFTS;

  // Режим правки существующей игры (И4→B1): savedOk — подтверждение после
  // успешного PUT; editLoading — индикатор загрузки payload игры перед первым
  // показом редактора.
  const [editLoading, setEditLoading] = useState(false);
  const [savedOk, setSavedOk] = useState(false);
  // Фаза 14 (эпик B4): информационное предупреждение «run не работает в открытых
  // квизах» после сохранения квиза с run-вопросами при активной открытой
  // публикации (§3.6 решение Д) — НЕ блокирующее, testid editor-run-open-warn.
  const [runOpenWarn, setRunOpenWarn] = useState(false);
  // Baseline-контент последнего сохранённого/открытого черновика (0.12.1): пока
  // правки не меняют сигнатуру контента, автосейв молчит — выбор черновика в
  // списке НЕ мутирует порядок (наверх поднимает только сохранение).
  const savedContentRef = useRef<string | null>(null);

  // ПУБЛИКАЦИЯ РЕШАЕТСЯ ПО ЧЕРНОВИКУ (B1): активный черновик привязан к серверному
  // квизу (draft.gameId) → PUT /api/games/{gameId} («Сохранить изменения»); не
  // привязан → POST новой игры. Привязка персистентна в localStorage, поэтому
  // PUT-режим переживает выход из редактора, прямой заход в /edit и переключение
  // черновиков (возвращение к привязанному снова даёт PUT).
  const editingThis = draft !== null && draft.gameId !== undefined;

  // Первый рендер: загрузить индекс; если черновиков нет — создать стартовый.
  // В режиме правки существующей игры (editGameId) этот bootstrap пропускаем —
  // загрузкой payload игры займётся отдельный эффект ниже.
  useEffect(() => {
    if (editGameId != null) return;
    const list = listDrafts();
    if (list.length === 0) {
      const d = createDraft(t('host.editor.defaultDraftTitle'));
      setDrafts(listDrafts());
      openDraft(d);
      return;
    }
    setDrafts(list);
    // «Последний открытый» хранится отдельно от сортировки (0.12.1): порядок
    // списка больше не мутируется просмотром, поэтому стартовый выбор — по явной
    // метке; при её отсутствии — самый свежий по updatedAt (list[0]).
    const lastOpenedId = getLastOpenedDraftId();
    const initial =
      (lastOpenedId !== null ? list.find((m) => m.id === lastOpenedId) : undefined) ?? list[0];
    const d = getDraft(initial.id);
    if (d !== null) openDraft(d);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Автосохранение с debounce — ТОЛЬКО реальных правок (0.12.1): пока сигнатура
  // контента совпадает с последним сохранённым/открытым состоянием, автосейв
  // молчит (выбор черновика не поднимает его наверх списка и не трогает
  // updatedAt). Правки → saveDraft обновляет updatedAt → запись поднимается наверх.
  useEffect(() => {
    if (!draft) return;
    const key = draftContentKey(draft);
    if (savedContentRef.current === key) return;
    const id = setTimeout(() => {
      saveDraft(draft);
      savedContentRef.current = key;
      setSavedAt(Date.now());
      setDrafts(listDrafts());
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [draft]);

  // Режим правки существующей игры (И4→B1): при появлении editGameId грузим квиз
  // (GET /api/games/{id}) и конвертируем wire-payload в черновик через мост
  // parseWireQuizPayload/wireQuizToDraft. Привязанный черновик (draft.gameId ===
  // editGameId) ПЕРЕИСПОЛЬЗУЕТСЯ: его содержимое актуализируется с сервера, id
  // сохраняется — повторные «Редактировать» не создают копий. Новому черновику
  // проставляется gameId: привязка персистентна, публикация будет PUT.
  useEffect(() => {
    if (editGameId == null) return;
    let cancelled = false;
    setEditLoading(true);
    setPublishError(null);
    setSavedOk(false);
    getGame(editGameId)
      .then((full) => {
        if (cancelled) return;
        const wire = parseWireQuizPayload(full.payload);
        const fromWire = wireQuizToDraft(wire, full.title);
        const existing = findDraftByGameId(editGameId);
        const d: QuizDraft = existing
          ? { ...existing, title: full.title, questions: fromWire.questions, gameId: editGameId }
          : { ...fromWire, gameId: editGameId };
        saveDraft(d);
        setDrafts(listDrafts());
        openDraft(d);
      })
      .catch((e) => {
        if (cancelled) return;
        setPublishError(errorMessage(e));
        // Мягкая деградация: сервер недоступен, но локальная копия привязанного
        // черновика есть — открываем её (правка остаётся возможной, PUT по gameId).
        const local = findDraftByGameId(editGameId);
        if (local) {
          setDrafts(listDrafts());
          openDraft(local);
        }
      })
      .finally(() => {
        if (!cancelled) setEditLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [editGameId]);

  function refreshDrafts(): void {
    setDrafts(listDrafts());
  }

  /**
   * Открывает черновик в редакторе БЕЗ пометки «изменён» (0.12.1): фиксирует
   * baseline контента (автосейв не сработает на нетронутый черновик — порядок
   * списка стабилен) и запоминает его как последний открытый (метка хранится
   * отдельно от сортировки индекса).
   */
  function openDraft(d: QuizDraft): void {
    savedContentRef.current = draftContentKey(d);
    setLastOpenedDraft(d.id);
    setCurrentId(d.id);
    setDraft(d);
  }

  /** Дружелюбное сообщение ошибки создания/импорта черновика: лимит — переводом,
   *  остальное — через errorMessage. */
  function draftLimitMessage(e: unknown): string {
    if (e instanceof Error && e.message === 'drafts_limit_reached') {
      return t('host.editor.draftsLimitReached', { max: MAX_DRAFTS });
    }
    return errorMessage(e);
  }

  function handleNewDraft(): void {
    try {
      const d = createDraft(t('host.editor.defaultDraftTitle'));
      refreshDrafts();
      openDraft(d);
      setPublishError(null);
    } catch (e) {
      setPublishError(draftLimitMessage(e));
    }
  }

  function handleSelectDraft(id: string): void {
    const d = getDraft(id);
    if (!d) return;
    openDraft(d);
    setPublishError(null);
  }

  function handleDeleteDraft(id: string): void {
    deleteDraft(id);
    const list = listDrafts();
    setDrafts(list);
    if (currentId === id) {
      if (list.length > 0) {
        const next = getDraft(list[0].id);
        if (next !== null) openDraft(next);
      } else {
        const d = createDraft(t('host.editor.defaultDraftTitle'));
        setDrafts(listDrafts());
        openDraft(d);
      }
    }
  }

  function handleImportFile(event: ChangeEvent<HTMLInputElement>): void {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    void file
      .text()
      .then((text) => {
        try {
          const d = importDraft(text);
          refreshDrafts();
          openDraft(d);
          setPublishError(null);
        } catch (e) {
          setPublishError(draftLimitMessage(e));
        }
      })
      .catch((e) => setPublishError(errorMessage(e)));
  }

  function handleExport(): void {
    if (!draft) return;
    downloadDraft(draft);
  }

  /**
   * Проверка условия предупреждения runOpenWarn (§3.6): черновик с run-вопросами
   * И активная открытая публикация привязанного квиза. Информационное — вызывается
   * после успешного PUT-сохранения; ошибки проверки не роняют сохранение.
   */
  async function checkRunOpenWarn(gameId: number, hasRun: boolean): Promise<void> {
    if (!hasRun) return;
    try {
      const status = await fetchOpenStatus(gameId);
      setRunOpenWarn(status.published);
    } catch {
      // Статус недоступен (сеть/auth) — молча: предупреждение опционально.
    }
  }

  async function handlePublish(): Promise<void> {
    if (!draft) return;
    setPublishing(true);
    setPublishError(null);
    setSavedOk(false);
    setRunOpenWarn(false);
    try {
      if (editingThis && draft.gameId !== undefined) {
        // Правка существующей игры (И4→B1): PUT /api/games/{draft.gameId} с
        // актуальным title и wire-payload. 200 → подтверждение «сохранено»
        // (без ухода с экрана). Привязка берётся из ПЕРСИСТЕНТНОГО поля черновика.
        await updateGame(draft.gameId, {
          title: draft.title.trim() || t('host.editor.untitledFallback'),
          payload: JSON.stringify(encodeDraftQuiz(draft)),
        });
        setSavedOk(true);
        onEditConsumed?.();
        // §3.6: run + активная открытая публикация → информационное предупреждение.
        await checkRunOpenWarn(
          draft.gameId,
          draft.questions.some((q) => q.body.kind === 'run'),
        );
      } else {
        // Новая игра (POST). 0.12.1: после публикации — переход в кабинет /me
        // (квиз появляется в «Мои квизы»); сессию запускает «Провести» из
        // кабинета, а не редактор автоматически.
        await createGameFromDraft(draft);
        onPublished();
      }
    } catch (e) {
      if (e instanceof ApiError && e.status === 429) {
        setPublishError(t('errors.createRateLimited'));
      } else {
        setPublishError(errorMessage(e));
      }
    } finally {
      setPublishing(false);
    }
  }

  // --- Мутации текущего черновика ---

  function updateTitle(title: string): void {
    setDraft((d) => (d ? { ...d, title } : d));
  }

  /**
   * Вставка вопросов из JSON (D4): валидированные parseDraftQuestionsJSON
   * вопросы (уже со свежими id) добавляются В КОНЕЦ списка текущего черновика.
   * Лимит limits.questionsPerQuiz проверяется в модале до вызова.
   */
  function handleInsertQuestions(newQuestions: DraftQuestion[]): void {
    setDraft((d) => (d ? { ...d, questions: [...d.questions, ...newQuestions] } : d));
  }

  return (
    <main className="card card--editor" aria-labelledby="editor-title">
      <header className="screen-head">
        <h2 id="editor-title" className="block-title">
          {t('host.editor.title')}
        </h2>
        <div className="screen-head-meta">
          {/* Ссылка «В кабинет» (0.12.1): постоянный выход в /me из редактора тем же
              компонентом/стилем, что «На главную». В режиме правки (editGameId) не
              рисуем её — там кнопка выхода уже ведёт в кабинет с той же подписью. */}
          {editGameId == null ? (
            <Link to="/me" className="btn btn--ghost" data-testid="editor-to-dashboard">
              {t('nav.toDashboard')}
            </Link>
          ) : null}
          <button type="button" className="btn btn--ghost" onClick={onExit}>
            {editGameId != null ? t('host.editor.toDashboard') : t('nav.toMain')}
          </button>
        </div>
      </header>

      <div className="editor-layout">
        <aside className="draft-sidebar">
          <button
            type="button"
            className="btn cta"
            onClick={handleNewDraft}
            disabled={draftsFull}
            data-testid="new-draft"
          >
            {t('host.editor.newDraft')}
          </button>
          <p className="muted small" data-testid="drafts-counter">
            {t('host.editor.draftsCount', { n: drafts.length, max: MAX_DRAFTS })}
          </p>
          {draftsFull ? (
            <p className="error small" role="alert" data-testid="drafts-limit">
              {t('host.editor.draftsLimitReached', { max: MAX_DRAFTS })}
            </p>
          ) : null}
          <ul className="draft-list">
            {drafts.map((m) => (
              <li
                key={m.id}
                className={`draft-item${m.id === currentId ? ' draft-item--active' : ''}`}
              >
                <button
                  type="button"
                  className="draft-item__open"
                  onClick={() => handleSelectDraft(m.id)}
                >
                  <span className="draft-item__title">{m.title || t('host.editor.untitled')}</span>
                  <span className="draft-item__meta">
                    {/* Бейдж привязки к серверному квизу (B1): публикация этого
                        черновика — PUT (правка существующей игры), не POST. */}
                    {m.gameId !== undefined ? (
                      <span
                        className="draft-item__linked"
                        data-testid="draft-linked"
                        title={t('host.editor.linkedBadge')}
                      >
                        🔗
                      </span>
                    ) : null}
                    {m.questionsCount !== undefined
                      ? t('host.editor.questionsShort', { n: m.questionsCount })
                      : '—'}
                  </span>
                </button>
                <button
                  type="button"
                  className="draft-item__del"
                  aria-label={t('host.editor.deleteDraftAria')}
                  onClick={() => handleDeleteDraft(m.id)}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
          <p className="muted small">{t('host.editor.draftsNote')}</p>
        </aside>

        <section className="editor-main">
          {draft ? (
            <>
              <div className="form-row">
                <label className="field-label" htmlFor="draft-title">
                  {t('host.editor.quizTitle')}
                </label>
                <input
                  id="draft-title"
                  className="text-input"
                  type="text"
                  value={draft.title}
                  maxLength={120}
                  placeholder={t('host.editor.titlePlaceholder')}
                  onChange={(e) => updateTitle(e.target.value)}
                />
              </div>

              <div className="editor-toolbar">
                <div className="editor-toolbar__group">
                  <label className="editor-preview-toggle">
                    <input
                      type="checkbox"
                      checked={showPreview}
                      onChange={(e) => setShowPreview(e.target.checked)}
                    />
                    {t('host.editor.previewToggle')}
                  </label>
                </div>
                <div className="editor-toolbar__group">
                  {/* JSON-предпросмотр + справочник типов (D3). */}
                  <button
                    type="button"
                    className="btn btn--ghost"
                    onClick={() => setJsonOpen(true)}
                    data-testid="editor-json-open"
                  >
                    {t('host.editor.json.open')}
                  </button>
                  {/* Вставка вопросов из JSON со строгой валидацией (D4). */}
                  <button
                    type="button"
                    className="btn btn--ghost"
                    onClick={() => setInsertOpen(true)}
                    data-testid="editor-json-insert-open"
                  >
                    {t('host.editor.insert.open')}
                  </button>
                  <span className="editor-saved">
                    {savedAt ? t('host.editor.saved') : t('host.editor.autosave')}
                  </span>
                </div>
              </div>
              <p className="muted small editor-hint">
                {t('host.editor.latexHintIntro')} <code>$…$</code>{' '}
                {t('host.editor.latexHintInline')} <code>$$…$$</code>{' '}
                {t('host.editor.latexHintBlock')} {t('host.editor.latexHintSuffix')}
              </p>

              {limits ? (
                <div className="editor-counters" data-testid="editor-limits">
                  <span
                    className={`muted small editor-counter${questionsFull ? ' editor-counter--full' : ''}`}
                    data-testid="editor-questions-counter"
                  >
                    {t('host.editor.limits.questionsCount', {
                      n: draft.questions.length,
                      limit: limits.questionsPerQuiz,
                    })}
                  </span>
                  {limits.quizImageTotalBytes > 0 ? (
                    <span className="muted small editor-counter" data-testid="editor-images-limit">
                      {t('host.editor.limits.imagesSize', {
                        used: formatBytes(0),
                        limit: formatBytes(limits.quizImageTotalBytes),
                      })}
                    </span>
                  ) : null}
                </div>
              ) : null}
              {questionsFull ? (
                <p className="error" role="alert" data-testid="editor-questions-full">
                  {t('host.editor.limits.questionsFull')}
                </p>
              ) : null}

              <QuizQuestionList
                questions={draft.questions}
                onChange={(qs) => setDraft((d) => (d ? { ...d, questions: qs } : d))}
                showPreview={showPreview}
                questionsLimit={limits?.questionsPerQuiz}
                codeMaxChars={limits?.codeMaxChars}
              />

              <div className="editor-actions">
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={handleExport}
                  disabled={draft.questions.length === 0}
                >
                  {t('host.editor.exportJson')}
                </button>
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {t('host.editor.importJson')}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/json,.json"
                  className="hidden-file"
                  onChange={handleImportFile}
                />
                <button
                  type="button"
                  className="cta"
                  onClick={() => void handlePublish()}
                  disabled={publishing || draft.questions.length === 0}
                  data-testid="publish-game"
                >
                  {publishing
                    ? editingThis
                      ? t('common.saving')
                      : t('common.creating')
                    : editingThis
                      ? t('host.editor.saveChanges')
                      : t('host.editor.create')}
                </button>
              </div>

              {publishError ? (
                <p className="error" role="alert" data-testid="rate-limit-error">
                  {publishError}
                </p>
              ) : null}
              {savedOk ? (
                <p className="q-hint q-hint--ok" role="status" data-testid="editor-saved-ok">
                  {t('host.editor.savedOk')}
                </p>
              ) : null}
              {runOpenWarn ? (
                <p className="editor-warn" role="status" data-testid="editor-run-open-warn">
                  ⚠ {t('host.editor.runOpenWarn')}
                </p>
              ) : null}

              {/* JSON-модалы (D3/D4) рендерятся в конец editor-main; сами они
                  portal'ятся в document.body (см. EditorJsonPanel/EditorJsonInsert). */}
              {jsonOpen ? (
                <EditorJsonPanel draft={draft} onClose={() => setJsonOpen(false)} />
              ) : null}
              {insertOpen ? (
                <EditorJsonInsert
                  currentCount={draft.questions.length}
                  questionsLimit={limits?.questionsPerQuiz}
                  onInsert={handleInsertQuestions}
                  onClose={() => setInsertOpen(false)}
                />
              ) : null}
            </>
          ) : editLoading ? (
            <p className="muted">{t('host.editor.loadingForEdit')}</p>
          ) : (
            <p className="muted">{t('host.editor.emptyHint')}</p>
          )}
        </section>
      </div>
    </main>
  );
}
